import React, {ReactElement} from "react";

import Image, {ImageObject} from "../image/Image";
import MarkdownText from "../markdown/markdown-text/MarkdownText";
import CTAButton, {CTA} from "../buttons/cta-button/CTAButton";
import Callout, {CalloutProps} from "../callout/Callout";
import TransitionBlock, {
  TRANSITION_BLOCK_FROM,
} from "../animation/transition-block/TransitionBlock";

import {css, generateKeyId} from "/src/utils/utils";

import * as styles from "./copy-module.module.scss";

interface CopyModuleProps {
  eyebrowImg?: ImageObject;
  eyebrow?: string;
  title: string;
  body?: string;
  cta?: CTA;
  callout?: CalloutProps;
  badges?: Array<ImageObject>;
  classes?: string;
  fullWidth?: boolean;
  darkMode?: boolean;
}

const CopyModule = (props: CopyModuleProps): ReactElement => {
  const {
    eyebrow,
    eyebrowImg,
    title,
    body,
    cta,
    callout,
    badges,
    classes,
    fullWidth,
    darkMode,
  } = props;

  const now = React.useRef(Date.now());

  const isDarkMode = darkMode === true;

  return (
    <div
      className={css(
        styles.copyModule,
        fullWidth ? styles.copyModuleFull : "",
        isDarkMode ? styles.copyModuleDark : "",
        classes!
      )}
    >
      {eyebrowImg && (
        <TransitionBlock
          key="EyebrowImage_Transition"
          triggerOnce
          from={TRANSITION_BLOCK_FROM.bottom}
          delay={0}
          transition="cubic-bezier(0.22, 0.61, 0.36, 1)"
        >
          <Image image={eyebrowImg} classes={styles.copyModule__eyebrowImg} />
        </TransitionBlock>
      )}

      {eyebrow && (
        <TransitionBlock
          key="EyebrowCopy_Transition"
          triggerOnce
          from={TRANSITION_BLOCK_FROM.bottom}
          delay={0}
          transition="cubic-bezier(0.22, 0.61, 0.36, 1)"
        >
          <p className={css("eyebrow", styles.copyModule__eyebrow)}>
            {eyebrow}
          </p>
        </TransitionBlock>
      )}

      {title && (
        <TransitionBlock
          key="Title_Transition"
          triggerOnce
          from={TRANSITION_BLOCK_FROM.bottom}
          delay={eyebrowImg || eyebrow ? 50 : 0}
          transition="cubic-bezier(0.22, 0.61, 0.36, 1)"
        >
          <h3 className={css("h3", styles.copyModule__title)}>{title}</h3>
        </TransitionBlock>
      )}

      {body && (
        <TransitionBlock
          key="Body__Transition"
          triggerOnce
          from={TRANSITION_BLOCK_FROM.bottom}
          delay={100}
          transition="cubic-bezier(0.22, 0.61, 0.36, 1)"
        >
          {body && (
            <MarkdownText text={body} classes={styles.copyModule__body} />
          )}
        </TransitionBlock>
      )}

      {cta || callout || badges ? (
        <TransitionBlock
          key="CTAs__Transition"
          triggerOnce
          from={TRANSITION_BLOCK_FROM.bottom}
          delay={150}
          transition="cubic-bezier(0.22, 0.61, 0.36, 1)"
        >
          {cta && (
            <CTAButton
              cta={cta}
              darkMode={isDarkMode}
              classes={styles.copyModule__cta}
              kind="primary"
            />
          )}

          {callout && callout.headline ? (
            <Callout
              {...callout}
              classes={styles.copyModule__badges__callout}
              darkMode={isDarkMode}
            />
          ) : null}

          {badges && badges.length > 0 && (
            <ul className={styles.copyModule__badges}>
              {badges.map((badge, i) => {
                return (
                  <li
                    className={styles.copyModule__badges__item}
                    key={generateKeyId(`copy-module-badge-item-${i}-${now}`)}
                  >
                    <TransitionBlock
                      triggerOnce
                      from={TRANSITION_BLOCK_FROM.bottom}
                      delay={i * 50}
                      transition="cubic-bezier(0.22, 0.61, 0.36, 1)"
                    >
                      <Image
                        image={badge}
                        classes={styles.copyModule__badges__img}
                      />
                    </TransitionBlock>
                  </li>
                );
              })}
            </ul>
          )}
        </TransitionBlock>
      ) : null}
    </div>
  );
};

export default CopyModule;
